/**
 * AnnouncementsPage - 공지사항 페이지
 *
 * AnnouncementsContext를 사용하여 상태를 관리합니다.
 * localStorage 연동 및 읽음 상태는 Context에서 처리됩니다.
 */
import { useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AnnouncementHeader,
  AnnouncementFilterTabs,
  AnnouncementListItem,
  AnnouncementDetailView,
  AnnouncementEmptyState,
  announcementTranslations,
} from './announcements';
import type { Announcement } from './announcements';
import { ROUTES, getAnnouncementDetailPath } from '@/config/routes';
import { useAnnouncements } from '@/contexts/AnnouncementsContext';
import { useLanguage } from '@/contexts/AppContext';

// 타입 재익스포트
export type { Announcement, AnnouncementsPageProps } from './announcements';

interface AnnouncementsPageComponentProps {
  onBack: () => void;
}

export function AnnouncementsPage({ onBack }: AnnouncementsPageComponentProps) {
  // #522: 15 locale 전수 지원 — language(3언어 축약) 대신 locale 로 lookup
  const { locale } = useLanguage();
  const { id: announcementIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Context에서 상태와 액션 가져오기
  const {
    filteredAnnouncements,
    selectedFilter,
    markAsRead,
    setFilter,
    getAnnouncementById,
  } = useAnnouncements();

  const t = announcementTranslations[locale];

  // URL에서 선택된 공지사항 파생
  const selectedAnnouncement = useMemo(() => {
    if (!announcementIdFromUrl) return null;
    return getAnnouncementById(announcementIdFromUrl);
  }, [announcementIdFromUrl, getAnnouncementById]);

  // URL 직접 접근 시 읽음 처리
  useEffect(() => {
    if (announcementIdFromUrl) {
      markAsRead(announcementIdFromUrl);
    }
  }, [announcementIdFromUrl, markAsRead]);

  // 공지사항 클릭 핸들러 (URL 네비게이션)
  const handleAnnouncementClick = useCallback((announcement: Announcement) => {
    markAsRead(announcement.id);
    navigate(getAnnouncementDetailPath(announcement.id));
  }, [markAsRead, navigate]);

  // 상세 뷰에서 뒤로가기 (URL 네비게이션)
  const handleBackFromDetail = useCallback(() => {
    navigate(ROUTES.ANNOUNCEMENTS);
  }, [navigate]);

  // 상세 뷰
  if (selectedAnnouncement) {
    return (
      <AnnouncementDetailView
        announcement={selectedAnnouncement}
        translations={t}
        onBack={handleBackFromDetail}
      />
    );
  }

  // 목록 뷰
  return (
    <div
      className="min-h-screen bg-muted"
      style={{ fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif" }}
    >
      {/* Header */}
      <AnnouncementHeader onBack={onBack} translations={t} />

      {/* Filter Tabs */}
      <AnnouncementFilterTabs
        selectedFilter={selectedFilter}
        onFilterChange={setFilter}
        translations={t}
      />

      {/* Announcements List */}
      <div className="p-4 pb-20">
        {filteredAnnouncements.length === 0 ? (
          <AnnouncementEmptyState translations={t} />
        ) : (
          <div className="space-y-2">
            {filteredAnnouncements.map((announcement) => (
              <AnnouncementListItem
                key={announcement.id}
                announcement={announcement}
                translations={t}
                onClick={handleAnnouncementClick}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
